import { useRef, useState } from "react";
import { Box, FormControl, Menu, MenuItem } from "@mui/material";
import { ReactComponent as DownArrowIcon } from "../../asserts/logo/downarrow.svg";

const FILTER_FIELD_SX = {
  height: 40,
  minHeight: 40,
  borderRadius: "4px",
  bgcolor: "#FFFFFF",
  fontFamily: "Lato, sans-serif",
  fontSize: { xs: 16, md: 14, xl: 16 },
  lineHeight: { xs: "24px", md: "20px", xl: "24px" },
  letterSpacing: 0,
  boxShadow: "0px 2px 3px 1px #55555533 inset",
  border: "1px solid #707070",
  display: "flex",
  alignItems: "center",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  pl: { xs: "12px", md: "10px", xl: "12px" },
  pr: { xs: "4px", md: "4px", xl: "4px" },
  gap: "4px",
  cursor: "pointer",
};

const filterControlSx = (fullWidthOnXs = false) => ({
  m: 0,
  flexShrink: 0,
  flex: {
    xs: fullWidthOnXs ? "1 1 100%" : "1 1 calc(50% - 4px)",
    sm: fullWidthOnXs ? "1 1 100%" : "1 1 calc(50% - 4px)",
    md: "0 1 auto",
  },
  width: {
    xs: fullWidthOnXs ? "100%" : "calc(50% - 4px)",
    sm: fullWidthOnXs ? "100%" : "calc(50% - 4px)",
    md: "auto",
  },
  minWidth: {
    xs: fullWidthOnXs ? "100%" : "calc(50% - 4px)",
    sm: fullWidthOnXs ? "100%" : "calc(50% - 4px)",
    md: "min-content",
  },
  maxWidth: {
    xs: fullWidthOnXs ? "100%" : "calc(50% - 4px)",
    md: "100%",
  },
});

export default function PortfolioFilterSelect({
  id,
  options,
  defaultValue,
  value: controlledValue,
  onChange,
  displayValue,
  fullWidthOnXs = false,
}) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value);
  const shownValue = displayValue ?? selectedOption?.label ?? value;

  const handleSelect = (nextValue) => {
    if (!isControlled) setInternalValue(nextValue);
    onChange?.(nextValue);
    setOpen(false);
  };

  const toggleOpen = () => setOpen((prev) => !prev);

  return (
    <FormControl sx={filterControlSx(fullWidthOnXs)}>
      <Box
        id={id}
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-label={`Open ${shownValue} menu`}
        aria-haspopup="listbox"
        aria-expanded={open ? "true" : "false"}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleOpen();
          }
        }}
        sx={{
          ...FILTER_FIELD_SX,
          width: { xs: "100%", md: "max-content" },
          maxWidth: "100%",
        }}
      >
        <Box
          component="span"
          sx={{
            flex: { xs: "1 1 auto", md: "0 1 auto" },
            minWidth: 0,
            maxWidth: { md: 280, xl: "none" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "#707070",
            userSelect: "none",
          }}
        >
          {shownValue}
        </Box>
        <Box
          component="span"
          aria-hidden
          sx={{
            flex: "0 0 auto",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: { xs: 32, xl: 32 },
            height: { xs: 32, xl: 32 },
            minWidth: 32,
            m: 0,
            p: 0,
            color: "var(--Schemes-On-Surface, #1D1B20)",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
            pointerEvents: "none",
          }}
        >
          <DownArrowIcon style={{ width: 24, height: 24, display: "block", flexShrink: 0 }} />
        </Box>
      </Box>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 0.5, minWidth: anchorRef.current?.offsetWidth || undefined } }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </FormControl>
  );
}
